import type { LayerContent, SceneGraph } from '../types/creative-template-example.types';
import { CANVAS_ASPECT_RATIOS } from '../helpers/creative-template.helpers';

interface SceneGraphPreviewProps {
  sceneGraph: SceneGraph;
  className?: string;
}

function renderLayerContent(content: LayerContent): React.ReactNode {
  switch (content.kind) {
    case 'fill':
      return null;
    case 'image':
      return content.media_url ? (
        <img
          src={content.media_url}
          alt={content.label}
          className="size-full object-cover"
        />
      ) : (
        <div className="flex size-full items-center justify-center bg-gray-200 text-[10px] text-gray-600">
          {content.label}
        </div>
      );
    case 'text':
      return (
        <div
          className="flex size-full items-center justify-center overflow-hidden p-1 text-center leading-tight"
          style={{
            color: content.text_color,
            fontFamily: content.font_family,
            fontSize: content.font_size ? `${content.font_size}px` : '0.75rem',
          }}
        >
          {content.content}
        </div>
      );
    case 'shape':
      return (
        <div
          className="size-full rounded-sm"
          style={{ backgroundColor: content.fill_color }}
        />
      );
    default:
      return null;
  }
}

export function SceneGraphPreview({ sceneGraph, className }: SceneGraphPreviewProps) {
  const aspectRatio = CANVAS_ASPECT_RATIOS[sceneGraph.canvas_class];

  return (
    <div className={className}>
      <div
        className="relative w-full overflow-hidden rounded-md border border-gray-400 bg-white shadow-sm"
        style={{ aspectRatio }}
      >
        {(sceneGraph.layers ?? []).map((layer) => {
          const content = layer.content;
          const backgroundColor = content?.kind === 'fill' ? content.fill_color : undefined;
          return (
            <div
              key={layer.role}
              className="absolute overflow-hidden"
              style={{
                left: `${layer.zone.x * 100}%`,
                top: `${layer.zone.y * 100}%`,
                width: `${layer.zone.w * 100}%`,
                height: `${layer.zone.h * 100}%`,
                backgroundColor,
              }}
            >
              {content ? renderLayerContent(content) : null}
            </div>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Live preview against canonical demo brand ({sceneGraph.canvas_class}).
      </p>
    </div>
  );
}
